import React, { useState, useEffect } from "react";
import {
  Control,
  FieldError,
  FieldErrorsImpl,
  Merge,
  useWatch,
} from "react-hook-form";
import { toast } from "react-toastify";
import { CustomPhoneNumber } from "./CustomPhoneNumber";
import PhoneValidationModal from "../molecules/PhoneValidationModal";

import {
  useStartPhoneValidation,
  useConfirmPhoneValidation,
  useGetPhoneValidationStatus,
} from "../../hooks/users";
import { SmsStatusEnum } from "../../constants/smsStatusEnum";

interface PhoneNumberWithVerificationProps {
  name: string;
  control: Control<any>;
  error?:
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | string
    | boolean;
  helperText?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined;
  placeholder?: string;
  defaultValue?: string;
  label?: string;
  labelVariant?: "label" | "title" | "none";
  labelClassName?: string;
  rules?: any;
  disabled?: boolean;
  maxLength?: number;
  defaultCountryCode?: string;

  userIdentification: string;
  entityIdentification: string;
  customerIdentification: string;

  userIsActive?: boolean;
  hasPhoneValidationAccess?: boolean;

  required?: boolean;
  onVerificationComplete?: () => void;
  onPhoneChange?: () => void;

  labels?: {
    verify?: string;
    verified?: string;
    verificationRequired?: string;
    pending?: string;
  };
}

export const PhoneNumberWithVerification: React.FC<
  PhoneNumberWithVerificationProps
> = ({
  name,
  control,
  error,
  helperText,
  placeholder,
  defaultValue,
  label,
  labelVariant,
  labelClassName,
  rules,
  disabled = false,
  maxLength = 13,
  defaultCountryCode,

  userIdentification,
  entityIdentification,
  customerIdentification,
  userIsActive = true,
  hasPhoneValidationAccess = true,
  required = false,
  onVerificationComplete,
  onPhoneChange,
  labels = {},
}) => {
  const phoneValue = useWatch({ control, name });

  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationExpiresAt, setVerificationExpiresAt] = useState(0);

  const { mutate: startValidation, isLoading: isStarting } =
    useStartPhoneValidation();
  const { mutate: confirmValidation, isLoading: isConfirming } =
    useConfirmPhoneValidation();

  const { data: validationData, refetch: refetchValidationData } =
    useGetPhoneValidationStatus(
      customerIdentification,
      entityIdentification,
      userIdentification
    );

  useEffect(() => {
    if (
      validationData?.status === SmsStatusEnum.PENDING &&
      phoneValue === validationData.phoneNumber &&
      validationData.expiredAt
    ) {
      setVerificationExpiresAt(validationData.expiredAt);
    }
  }, [validationData, phoneValue]);

  const handleStartVerification = () => {
    if (!userIsActive) {
      toast.error("Phone verification is only available for active users.");
      return;
    }

    if (!phoneValue || !phoneValue.startsWith("+")) {
      toast.error("Please enter a valid phone number with country code.");
      return;
    }

    startValidation(
      {
        customer_identification: customerIdentification,
        entityIdentification,
        user_identification: userIdentification,
      },
      {
        onSuccess: (data) => {
          const expiresAt =
            data?.data?.element?.expiredAt || Date.now() + 5 * 60 * 1000;
          setVerificationExpiresAt(expiresAt);
          setShowVerificationModal(true);
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.message ||
            "Failed to send verification code";
          toast.error(message);
        },
      }
    );
  };

  const handleConfirmVerification = (code: string) => {
    confirmValidation(
      {
        customer_identification: customerIdentification,
        entityIdentification,
        user_identification: userIdentification,
        code,
      },
      {
        onSuccess: () => {
          setShowVerificationModal(false);
          refetchValidationData();
          onVerificationComplete?.();
        },
      }
    );
  };

  const handleResendVerification = () => {
    handleStartVerification();
  };

  return (
    <div className="relative">
      <CustomPhoneNumber
        name={name}
        control={control}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        defaultValue={defaultValue}
        label={label}
        labelVariant={labelVariant}
        labelClassName={labelClassName}
        rules={rules}
        disabled={disabled}
        maxLength={maxLength}
        defaultCountryCode={defaultCountryCode}
      />

      <PhoneValidationModal
        show={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        onConfirm={handleConfirmVerification}
        onResend={handleResendVerification}
        expiresAt={verificationExpiresAt}
        loading={isStarting || isConfirming}
        phoneNumber={phoneValue}
      />
    </div>
  );
};
